import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleNodes,
  faVolcano,
  faLocationDot,
  faSiren,
  faSparkles,
  faTowerBroadcast,
  faWavePulse,
  faUpload,
} from "@fortawesome/sharp-regular-svg-icons"
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft"
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight"
import { IconButton, Typography, useMediaQuery } from "@mui/material"
import MuiDrawer from "@mui/material/Drawer"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import type { CSSObject, Theme } from "@mui/material/styles"
import { styled, useTheme } from "@mui/material/styles"
import type React from "react"
import { useCallback, useImperativeHandle, type Ref } from "react"
import { Link, useLocation } from "react-router"
import PhcAshcaLarge from "../assets/svg-components/PhcAshcaLarge"
import PhcAshcaSmall from "../assets/svg-components/PhcAshcaSmall"
import { useNavDrawerOpen } from "../hooks/useNavDrawerOpen"
import { useRouteAccess } from "../hooks/useRouteAccess"
import { ROUTES } from "../utils/constants"
import { objectKeys } from "../utils/helpers"
import { extraColors } from "../utils/theme"
import { BetaTag } from "./BetaTag"

const drawerWidth = 250
const ICON_COLOR = extraColors.status.navyLight

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== "open",
})(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  cursor: "pointer",
  border: "none",
  "& .MuiPaper-root": {
    border: "none",
    borderRight: `1px solid ${extraColors.status.navy}`,
    boxShadow: "0px 3px 7px 1px rgba(0, 0, 0, 0.2)",
    background: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(40, 115, 174, 0.4) 50%, rgba(24, 52, 94, 0.4) 100%), #1D2229`,
  },
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
    "& .phc-logo": {
      height: 37,
      width: 52,
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .phc-logo": {
      height: 20,
      width: 30,
    },
  }),
}))

const DrawerButton = styled(IconButton)<{ isopen: number }>(({ theme }) => ({
  "&:hover, &.Mui-focusVisible, &:active": {
    background: extraColors.translucentWhite,
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
  color: ICON_COLOR,
}))

const NavContainer = styled(List)(() => ({
  zIndex: 999,
  display: "flex",
  flexDirection: "column",
  height: "100%",
}))

const NavItemList = styled("nav")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 24,
}))

const NavItem = styled(ListItem)<{ isopen: number }>(({ isopen }) => ({
  height: 38,
  width: "auto",
  padding: 0,
  margin: isopen ? 0 : "0 5px",
  borderRadius: isopen ? 0 : 6,
  overflow: "hidden",
}))

const NavItemButton = styled(ListItemButton)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  "&.Mui-selected": {
    background: extraColors.translucentWhite,
    boxShadow: "inset 0px 1px 3px rgba(0, 0, 0, 0.12)",
  },
  "&:hover": {
    background: extraColors.translucentWhite,
  },
}) as typeof ListItemButton

const NavItemIcon = styled(ListItemIcon)<{ iconcolor: string }>(
  ({ iconcolor }) => ({
    fill: iconcolor,
    color: iconcolor,
    padding: 0,
    minWidth: 25,
    marginRight: 10,
  })
)

const NavItemContent = styled("div")({
  flex: 1,
  textAlign: "left",
})

const LogoButton = styled(Link)<{ isopen: number }>(({ isopen }) => ({
  maxWidth: 140,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  height: isopen ? 88 : 24,
  width: isopen ? "100%" : 50,
  "&:hover": {
    background: "transparent",
  },
}))

export const TOOLBAR_HEIGHT = 59

const openedMixin = (theme: Theme): CSSObject => ({
  minWidth: "100%",
  top: TOOLBAR_HEIGHT,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    top: 0,
    maxWidth: drawerWidth,
    minWidth: drawerWidth,
    width: drawerWidth,
  },
})

const closedMixin = (theme: Theme): CSSObject => ({
  top: TOOLBAR_HEIGHT,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflow: "hidden",
  width: "0px",
  [theme.breakpoints.up("md")]: {
    top: 0,
    width: `calc(${theme.spacing(8)} + 2px)`,
  },
})

const TopContainer = styled("div")<{ isopen: number }>(({ isopen, theme }) => ({
  height: 88,
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  flexDirection: isopen ? "row" : "column",
  gap: 12,
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}))

interface NavItem {
  title: string
  isBeta?: boolean
  icon: React.ReactElement
}

const NAV_ITEMS: Partial<Record<keyof typeof ROUTES, NavItem>> = {
  PULSE: {
    title: "Pulse",
    icon: <FontAwesomeIcon icon={faWavePulse} />,
  },
  BIOTHREAT_SIGNALS: {
    title: "Biothreat Signals",
    icon: <FontAwesomeIcon icon={faTowerBroadcast} />,
    isBeta: true,
  },
  WASTEWATER_OAHU: {
    title: "Oahu",
    isBeta: true,
    icon: <FontAwesomeIcon icon={faVolcano} />,
  },
  WINGMAN: {
    title: "Wingman",
    isBeta: true,
    icon: <FontAwesomeIcon icon={faSparkles} />,
  },
  BIO_MESH: {
    title: "BioMesh",
    isBeta: true,
    icon: <FontAwesomeIcon icon={faCircleNodes} />,
  },
  COP: {
    title: "Update COP",
    isBeta: true,
    icon: <FontAwesomeIcon icon={faUpload} />,
  },
  ASSETS: {
    title: "Locations",
    icon: <FontAwesomeIcon icon={faLocationDot} />,
  },
  CRITICAL_THREATS: {
    title: "Critical Threats",
    icon: <FontAwesomeIcon icon={faSiren} />,
  },
}

const DrawerNav = ({ ref }: { ref?: Ref<{ toggle(): void }> }) => {
  const { isOpen, setIsOpen } = useNavDrawerOpen()

  const toggleDrawer = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen, setIsOpen])

  useImperativeHandle(ref, () => ({
    toggle() {
      toggleDrawer()
    },
  }))

  return (
    <div style={{ gridArea: "nav" }}>
      <Drawer
        variant="permanent"
        sx={t => ({
          marginTop: 0,
          width: 0, // hide drawer on mobile viewports
          height: isOpen ? `calc(100vh - ${TOOLBAR_HEIGHT}px)` : 0,
          [t.breakpoints.up("md")]: {
            maxWidth: drawerWidth,
          },
        })}
        open={isOpen}
      >
        <NavContainer>
          <TopContainer isopen={+isOpen}>
            <LogoButton to={ROUTES.PULSE} isopen={+isOpen}>
              {isOpen ? <PhcAshcaLarge /> : <PhcAshcaSmall />}
            </LogoButton>
            <DrawerButton onClick={toggleDrawer} isopen={+isOpen}>
              {isOpen ? (
                <KeyboardDoubleArrowLeftIcon />
              ) : (
                <KeyboardDoubleArrowRightIcon />
              )}
            </DrawerButton>
          </TopContainer>
          <NavigationItems isOpen={isOpen} setIsOpen={setIsOpen} />
        </NavContainer>
      </Drawer>
    </div>
  )
}

export default DrawerNav

const Line = styled("div")(({ theme }) => ({
  display: "flex",
  width: "calc(100% - 16px)",
  height: 1,
  background: extraColors.status.navy,
  margin: "0 8px",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}))

const NavigationItems: React.FC<{
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}> = ({ isOpen, setIsOpen }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { canAccessRoute } = useRouteAccess()
  const location = useLocation()

  const handleLink = (key: keyof typeof ROUTES) => {
    return ROUTES[key]
  }

  return (
    <NavItemList
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <Line />
      {objectKeys(NAV_ITEMS)
        .filter(item => canAccessRoute(item))
        .map(item => {
          const content = NAV_ITEMS[item]
          if (!content) return null
          const { title, icon: Icon, isBeta } = content
          return (
            <NavItem key={item} isopen={+isOpen}>
              <NavItemButton
                component={Link}
                to={{
                  pathname: handleLink(item),
                  search: "",
                }}
                selected={location.pathname === handleLink(item)}
                onClick={() => {
                  if (isMobile) {
                    setIsOpen(false)
                  }
                }}
              >
                <NavItemIcon
                  iconcolor={
                    location.pathname === handleLink(item)
                      ? extraColors.white
                      : ICON_COLOR
                  }
                >
                  {Icon}
                </NavItemIcon>
                {isOpen && (
                  <NavItemContent>
                    <Typography variant={"body1"}>{title}</Typography>
                  </NavItemContent>
                )}
                {isBeta && isOpen ? <BetaTag /> : null}
              </NavItemButton>
            </NavItem>
          )
        })}
    </NavItemList>
  )
}
